import type { Lineups, TeamLineup } from "../../../../../../../types/teams.types";

export interface Mentionable {
	id: string;
	fullName: string;
	lastName: string;
	teamName: string;
	role: string;
	number?: number;
	colour?: string;
}

export interface MentionSection {
	teamName: string;
	items: Mentionable[];
}

export interface MentionMatch {
	query: string;
	start: number;
}

const POSITION_LABELS: Record<string, string> = {
	G: "GK",
	D: "DEF",
	M: "MID",
	F: "FWD",
};

const getLastName = (name: string): string => {
	const parts = name.split(" ");
	if (parts.length <= 1) return name;
	if (/^[A-Z]\.$/.test(parts[0])) return parts.slice(1).join(" ");
	return parts[parts.length - 1];
};

const fromTeam = (team: TeamLineup): Mentionable[] => {
	const colour = team.colors?.player.primary;

	return [
		{
			id: `team-${team.teamId}`,
			fullName: team.teamName,
			lastName: team.teamName,
			teamName: team.teamName,
			role: "Team",
			colour,
		},
		...team.startXI.map((p) => ({
			id: `player-${p.id}`,
			fullName: p.name,
			lastName: getLastName(p.name),
			teamName: team.teamName,
			role: POSITION_LABELS[p.position] ?? p.position,
			number: p.number,
			colour,
		})),
		...team.substitutes.map((p) => ({
			id: `sub-${p.id}`,
			fullName: p.name,
			lastName: getLastName(p.name),
			teamName: team.teamName,
			role: "Sub",
			number: p.number,
			colour,
		})),
		{
			id: `coach-${team.teamId}`,
			fullName: team.coach,
			lastName: getLastName(team.coach),
			teamName: team.teamName,
			role: "Coach",
			colour,
		},
	];
};

export const buildMentionSections = (lineups: Lineups | null): MentionSection[] => {
	if (!lineups) return [];

	return [
		{ teamName: lineups.home.teamName, items: fromTeam(lineups.home) },
		{ teamName: lineups.away.teamName, items: fromTeam(lineups.away) },
	];
};

const MENTION_PATTERN = /(?:^|\s)@([a-zA-ZÀ-ž.\d]*)$/;

export const getMentionMatch = (text: string, cursorPos: number): MentionMatch | null => {
	const before = text.slice(0, cursorPos);
	const match = before.match(MENTION_PATTERN);
	if (!match) return null;

	const query = match[1];
	return { query, start: cursorPos - query.length - 1 };
};
